import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin, Phone, CheckCircle, UserCheck, Zap, Clock, ShieldCheck, Calculator, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import CostSimulator from '../components/CostSimulator';
import { SEOHead, localBusinessSchema, faqSchema } from '../components/SEOHead';
import { AGENCIES } from '../lib/agencies-data';
import { getNearbyCities, cityPath, SAP_NUMBER } from '../lib/cities-data';
import { useCity } from '../hooks/useCity';
import { CityPageSkeleton } from '../components/CitySearch';
import { SERVICE_CITY_CONFIG, serviceCityPath } from '../lib/service-city-data';
import { COMPANY_INFO, formatPrice } from '../lib/utils';
import { SeasonalLocalContent } from '../components/SeasonalLocalContent';
import { VIEWPORT_ONCE } from '../lib/motion';

const SITE_URL = 'https://ptitservices.com';
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] } }
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

export default function ServiceCityPage({ serviceKey }) {
  const { citySlug } = useParams();
  const { city, loading } = useCity(citySlug);
  const cfg = SERVICE_CITY_CONFIG[serviceKey];
  const agency = AGENCIES['artois.ptitservices'];

  if (loading) return <CityPageSkeleton />;
  if (!city || !cfg) return <Navigate to="/services-a-domicile" replace />;

  const relatedKeys = (cfg.related || Object.keys(SERVICE_CITY_CONFIG).filter((k) => k !== serviceKey)).filter((k) => SERVICE_CITY_CONFIG[k]);
  const nearby = getNearbyCities(city, 10);
  const path = serviceCityPath(serviceKey, city);
  const faq = cfg.faq(city.name, city.region);
  const regionText = cfg.regionCopy[city.region]
    || (city.dynamic ? `${city.name} est un village${city.cp ? ` (${city.cp})` : ''} situé à ${city.distanceKm ? `${String(city.distanceKm).replace('.', ',')} km` : 'moins de 30 km'} de notre agence de Servins. Comme dans toutes les communes rurales de l'Artois, nos Easers s'y déplacent avec le même sérieux que dans les grandes villes : pas de frais cachés, un intervenant attitré et des créneaux réguliers adaptés aux maisons avec jardin.` : '');
  const Icon = cfg.icon;

  // Example card: regular (hours/week x 4 -> monthly) or ponctuel (one-off intervention)
  const example = cfg.example || { mode: 'regulier', hours: cfg.exampleHours || 2 };
  const exampleHours = example.mode === 'ponctuel' ? example.hours : example.hours * 4;
  const exampleRate = example.mode === 'ponctuel' ? cfg.pricePonctuel : cfg.priceBefore;
  const monthlyBefore = exampleRate * exampleHours;
  const monthlyAfter = monthlyBefore / 2;
  const exampleSub = example.mode === 'ponctuel' ? `${example.hours}h en intervention ponctuelle${example.label ? ` — ${example.label}` : ''}` : `${example.hours}h par semaine, tarif régulier`;
  const exampleCostLabel = example.mode === 'ponctuel' ? `Coût de l'intervention (${exampleHours}h)` : `Coût mensuel (${exampleHours}h)`;
  const examplePayLabel = example.mode === 'ponctuel' ? 'Vous payez' : 'Vous payez chaque mois';

  const title = cfg.title(city.name, city.cp);
  const description = cfg.description(city.name);

  const serviceSchemaLocal = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_URL}${path}`,
    "name": cfg.schemaName ? cfg.schemaName(city.name) : `${cfg.label} à ${city.name}`,
    "serviceType": cfg.serviceType || cfg.label,
    "description": description,
    "url": `${SITE_URL}${path}`,
    "provider": { ...localBusinessSchema(agency), "@context": undefined },
    "areaServed": { "@type": "City", "name": city.name, "address": { "@type": "PostalAddress", "addressLocality": city.name, "postalCode": city.cp || undefined, "addressCountry": "FR" } },
    "offers": {
      "@type": "Offer",
      "price": cfg.priceAfter,
      "priceCurrency": "EUR",
      "priceSpecification": { "@type": "UnitPriceSpecification", "price": cfg.priceAfter, "priceCurrency": "EUR", "unitText": "heure", "description": "Tarif après avance immédiate du crédit d'impôt 50% (tarif plein : " + cfg.priceBefore + " €/h)" },
      "availability": "https://schema.org/InStock"
    },
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": SITE_URL },
      { "@type": "ListItem", "position": 2, "name": "Villes desservies", "item": `${SITE_URL}/services-a-domicile` },
      { "@type": "ListItem", "position": 3, "name": city.name, "item": `${SITE_URL}${cityPath(city)}` },
      { "@type": "ListItem", "position": 4, "name": `${cfg.label} à ${city.name}`, "item": `${SITE_URL}${path}` },
    ]
  };

  return (
    <div data-testid={`service-city-page-${serviceKey}`}>
      <SEOHead title={title} description={description} path={path} schema={[serviceSchemaLocal, faqSchema(faq), breadcrumb]} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[linear-gradient(135deg,hsla(195,20%,97%,1)_0%,hsla(182,40%,94%,1)_55%,hsla(195,20%,97%,1)_100%)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6 flex-wrap" aria-label="Fil d'Ariane">
            <Link to="/" className="hover:text-foreground">Accueil</Link><span>/</span>
            <Link to="/services-a-domicile" className="hover:text-foreground">Villes desservies</Link><span>/</span>
            <Link to={cityPath(city)} className="hover:text-foreground">{city.name}</Link><span>/</span>
            <span className="text-foreground font-medium">{cfg.label}</span>
          </nav>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <motion.div className="lg:col-span-7" initial="hidden" animate="visible" variants={stagger}>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-2 mb-4">
                <Badge className="px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"><Icon className="h-4 w-4 mr-2" /> {cfg.label} à {city.name}</Badge>
                <Badge variant="secondary" className="px-3 py-2 rounded-full"><MapPin className="h-3.5 w-3.5 mr-1" /> {city.region}</Badge>
              </motion.div>
              <motion.h1 variants={fadeUp} className="font-['Space_Grotesk'] text-3xl sm:text-4xl lg:text-[2.75rem] font-semibold tracking-tight leading-tight mb-4" data-testid="service-city-title">
                {cfg.h1(city.name)}
              </motion.h1>
              <motion.p variants={fadeUp} className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-6 max-w-xl">
                {city.intro}
              </motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3 mb-8">
                <div className="px-4 py-2.5 rounded-xl bg-card border border-border shadow-sm" data-testid="service-city-price">
                  <span className="text-xs text-muted-foreground block">Tarif régulier — à partir de</span>
                  <span className="text-sm line-through text-muted-foreground">{formatPrice(cfg.priceBefore)}/h</span>
                  <span className="text-lg font-bold text-[hsl(var(--primary))] ml-2">{formatPrice(cfg.priceAfter)}/h</span>
                  <span className="text-xs text-muted-foreground block">après avance immédiate du crédit d'impôt</span>
                </div>
                <div className="px-4 py-2.5 rounded-xl bg-card border border-border shadow-sm">
                  <span className="text-xs text-muted-foreground block">Ponctuel sans engagement</span>
                  <span className="text-sm line-through text-muted-foreground">{formatPrice(cfg.pricePonctuel)}/h</span>
                  <span className="text-lg font-bold text-[hsl(var(--primary))] ml-2">{formatPrice(cfg.pricePonctuel / 2)}/h</span>
                  <span className="text-xs text-muted-foreground block">minimum 2h</span>
                </div>
              </motion.div>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <Link to="/devis" state={{ service: cfg.quoteService, ville: city.name, cp: city.cp || '' }} data-testid="service-city-quote-button">
                  <Button size="lg" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white shadow-lg">
                    Devis {cfg.label.toLowerCase()} gratuit à {city.name} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <a href={`tel:${COMPANY_INFO.phoneRaw}`}>
                  <Button size="lg" variant="outline"><Phone className="mr-2 h-4 w-4" /> {COMPANY_INFO.phone}</Button>
                </a>
              </motion.div>
            </motion.div>
            <motion.div className="lg:col-span-5" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }}>
              <Card className="p-6 shadow-xl border-border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center"><Calculator className="h-5 w-5 text-[hsl(var(--primary))]" /></div>
                  <div>
                    <h2 className="font-['Space_Grotesk'] font-semibold">Exemple concret à {city.name}</h2>
                    <p className="text-xs text-muted-foreground">{exampleSub}</p>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">{exampleCostLabel}</span><span className="font-semibold">{formatPrice(monthlyBefore)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Crédit d'impôt 50%</span><span className="font-semibold text-[hsl(var(--primary))]">- {formatPrice(monthlyAfter)}</span></div>
                  <div className="flex justify-between items-center p-3 rounded-lg bg-[hsl(var(--primary))]/10 mt-2"><span className="font-semibold">{examplePayLabel}</span><span className="text-xl font-bold text-[hsl(var(--primary))]" data-testid="service-city-example-total">{formatPrice(monthlyAfter)}</span></div>
                </div>
                <ul className="mt-4 space-y-2">
                  {[
                    { icon: UserCheck, text: `Un Easer dédié pour votre domicile à ${city.name}` },
                    { icon: Zap, text: 'Avance immédiate Urssaf : aucune avance de frais' },
                    { icon: Clock, text: 'Rappel sous 24h ouvrées, évaluation gratuite' },
                    { icon: ShieldCheck, text: `Déclaré Services à la Personne — ${SAP_NUMBER}` },
                  ].map((it) => (
                    <li key={it.text} className="flex items-start gap-2 text-xs text-muted-foreground"><it.icon className="h-4 w-4 text-[hsl(var(--primary))] flex-shrink-0" />{it.text}</li>
                  ))}
                </ul>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Prestations */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div className="text-center mb-10" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Prestations</Badge>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3">{cfg.prestationsTitle(city.name)}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Régulier ou ponctuel, toujours avec le même Easer attitré. Toutes nos prestations sont éligibles au crédit d'impôt de 50%.</p>
          </motion.div>
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger} data-testid="service-city-prestations">
            {cfg.prestations.map((p) => (
              <motion.div key={p.title} variants={fadeUp}>
                <Card className="p-5 h-full border border-border shadow-sm">
                  <CheckCircle className="h-5 w-5 text-[hsl(var(--primary))] mb-2" />
                  <h3 className="font-['Space_Grotesk'] font-semibold text-sm mb-1">{p.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
          <div className="flex flex-wrap gap-2 mt-6 justify-center">
            {cfg.serviceLinks.map((s) => (
              <Link key={s.id} to={`/services/${s.id}`} className="px-3 py-1.5 rounded-full border border-border bg-card text-sm hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors">{s.label} <ArrowRight className="inline h-3 w-3 ml-1" /></Link>
            ))}
          </div>
        </div>
      </section>

      {/* Local copy + why */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[hsl(var(--secondary))]/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">{cfg.label} à {city.name}</Badge>
              <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-4">
                {cfg.localTitle ? cfg.localTitle(city.name) : `${cfg.label} à ${city.name}`}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4" data-testid="service-city-region-copy">{regionText}</p>
              <p className="text-muted-foreground leading-relaxed">
                {cfg.localCopy ? cfg.localCopy(city.name) : `Chez Ptitservices, votre Easer à ${city.name} est salarié, formé et suivi par notre agence de Servins. Nous gérons le contrat, l'avance immédiate Urssaf et les remplacements.`}
              </p>
            </motion.div>
            <motion.div className="grid grid-cols-1 sm:grid-cols-2 gap-4" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger}>
              {[
                { icon: UserCheck, title: 'Easer attitré', desc: 'Toujours la même personne chez vous : confiance et continuité.' },
                { icon: Zap, title: 'Avance immédiate', desc: 'Le crédit d\'impôt est déduit de chaque facture, sans attendre.' },
                { icon: Sparkles, title: 'Tarifs transparents', desc: '« À partir de », sans frais cachés : le devis fait foi.' },
                { icon: Clock, title: 'Souplesse', desc: 'Régulier à tarif préférentiel ou ponctuel sans engagement.' },
              ].map((it) => (
                <motion.div key={it.title} variants={fadeUp}>
                  <Card className="p-5 h-full">
                    <div className="w-9 h-9 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center mb-3"><it.icon className="h-4 w-4 text-[hsl(var(--primary))]" /></div>
                    <h3 className="font-semibold text-sm mb-1">{it.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{it.desc}</p>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Seasonal local content (unique per city, service & season, AI-written) */}
      <SeasonalLocalContent pageType={serviceKey} citySlug={city.slug} cityName={city.name} />

      {/* Simulator */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Simulateur</Badge>
              <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-4">{cfg.simTitle ? cfg.simTitle(city.name) : `Combien coûte ${cfg.label.toLowerCase()} à ${city.name} ?`}</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">Ajustez les heures et la fréquence : le simulateur calcule votre reste à charge réel après le crédit d'impôt de 50%. Vos choix sont ensuite transférés dans la demande de devis.</p>
              <Link to={cityPath(city)} className="text-sm text-[hsl(var(--primary))] font-medium inline-flex items-center gap-1 hover:underline">Tous nos services à {city.name} <ArrowRight className="h-3.5 w-3.5" /></Link>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <CostSimulator preselectedTab={cfg.simulatorTab} ville={city.name} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[hsl(var(--secondary))]/60">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <motion.div className="text-center mb-10" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">FAQ</Badge>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight">Questions fréquentes — {cfg.label.toLowerCase()} à {city.name}</h2>
          </motion.div>
          <Accordion type="single" collapsible className="space-y-3" data-testid="service-city-faq">
            {faq.map((item, i) => (
              <AccordionItem key={item.q} value={`faq-${i}`} className="border border-border rounded-lg px-4 bg-card">
                <AccordionTrigger className="text-sm font-medium text-left hover:no-underline">{item.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Cross links */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {relatedKeys.map((rk) => {
              const rc = SERVICE_CITY_CONFIG[rk];
              const RIcon = rc.icon;
              return (
                <Card key={rk} className="p-5 flex items-center gap-4 border-2 border-[hsl(var(--primary))]/15 bg-[hsl(var(--brand-mint))]/20">
                  <div className="w-11 h-11 rounded-lg bg-card flex items-center justify-center flex-shrink-0"><RIcon className="h-5 w-5 text-[hsl(var(--primary))]" /></div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-['Space_Grotesk'] font-semibold text-sm">{rc.crossTitle ? rc.crossTitle(city.name) : `${rc.label} à ${city.name}`}</h2>
                    <p className="text-xs text-muted-foreground">Un seul prestataire, une seule facture, le même crédit d'impôt.</p>
                  </div>
                  <Link to={serviceCityPath(rk, city)} data-testid="service-city-cross-link"><Button variant="outline" size="sm">Voir <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
                </Card>
              );
            })}
          </div>

          {nearby.length > 0 && (
            <div>
              <h2 className="font-['Space_Grotesk'] text-xl sm:text-2xl font-semibold tracking-tight mb-2">{cfg.label} près de {city.name}</h2>
              <p className="text-sm text-muted-foreground mb-4">{city.region} — nos Easers interviennent aussi dans ces communes :</p>
              <div className="flex flex-wrap gap-2" data-testid="service-city-nearby">
                {nearby.map((c) => (
                  <Link key={c.slug} to={serviceCityPath(serviceKey, c)} className="px-3 py-1.5 rounded-full border border-border bg-card text-sm hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors">
                    {cfg.shortLabel || cfg.label} à {c.name}
                  </Link>
                ))}
                <Link to="/services-a-domicile" className="px-3 py-1.5 rounded-full bg-[hsl(var(--primary))] text-white text-sm hover:bg-[hsl(var(--primary))]/90 transition-colors flex items-center gap-1">Toutes les villes <ArrowRight className="h-3.5 w-3.5" /></Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[hsl(var(--primary))] text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Icon className="h-8 w-8 mx-auto mb-4 opacity-80" />
          <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-4">{cfg.ctaTitle ? cfg.ctaTitle(city.name) : `${cfg.label} à ${city.name} : demandez votre devis`}</h2>
          <p className="text-primary-foreground/80 mb-8 max-w-xl mx-auto">Devis gratuit, sans engagement, réponse sous 24h ouvrées. {formatPrice(cfg.priceAfter)}/h après avance immédiate du crédit d'impôt.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/devis" state={{ service: cfg.quoteService, ville: city.name, cp: city.cp || '' }}>
              <Button size="lg" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white shadow-lg">Demander un devis <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </Link>
            <a href={`tel:${COMPANY_INFO.phoneRaw}`}><Button size="lg" variant="secondary"><Phone className="mr-2 h-4 w-4" /> {COMPANY_INFO.phone}</Button></a>
          </div>
        </div>
      </section>
    </div>
  );
}
